import { useState } from 'react';
import { CaretRightIcon, ReceiptIcon } from '@phosphor-icons/react';
import { getCategoryMeta, type CategoryIndex } from '@/components/transactions/screen/categoryIndex';
import { DayHeader } from '@/components/transactions/screen/DayHeader';
import { TransactionRowSkeleton } from '@/components/transactions/screen/TransactionRowSkeleton';
import { TRANSACTION_LINE_INTERACTIVE } from '@/components/transactions/TransactionLine';
import { TransactionListCard, TransactionListEmpty } from '@/components/transactions/TransactionListCard';
import { useCategoryIndex } from '@/hooks/useCategoryIndex';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useSpacePerspective } from '@/hooks/useSpacePartnerPreview';
import { useSpaceExpensesPage } from '@/hooks/queries/useSpaceExpensesPage';
import { EXPENSES_PAGE_SIZE, expensePayerName, type SpaceExpenseRow } from '@/lib/spaces/expenses';
import { isOverview, readFailed } from '@/lib/spaces/home';
import { memberPhoto } from '@/lib/spaces/settlement';
import { type DayGroup, groupItemsByDay } from '@/lib/transactions/utils';
import type { SpaceDetail } from '@/types/spaces';
import { SpaceExpenseLine } from './SpaceExpenseLine';
import { ExpensePills } from './SpaceExpenseParts';
import { SettlementFailure, SettlementPagination } from './SettlementParts';
import { SpacePrivate } from './SpacePartnerPreview';

const REGION = 'Lista de despesas';

// The personal transactions list over the space's expenses: the same card, the same day headers (a neutral day total —
// every amount here is spend) and the same line; the space only adds its facts to the meta and the split column.
export function SpaceExpenseList({ space, userId, period, filter, onOpen }: Readonly<{
  space: SpaceDetail; userId: string; period: string; filter: string; onOpen: (expense: SpaceExpenseRow) => void;
}>) {
  const [offset, setOffset] = useState(0);
  const page = useSpaceExpensesPage(space.id, period, filter, offset);
  const categoryIndex = useCategoryIndex();
  if (readFailed(page, isOverview)) return <TransactionListCard aria-label={REGION}><div className="p-5"><SettlementFailure message="Não foi possível carregar as despesas." retry={page.mutate} /></div></TransactionListCard>;
  if (!page.data) return <TransactionListCard aria-label={REGION}><ul className="divide-y divide-border" role="status" aria-label="Carregando despesas"><li><TransactionRowSkeleton /></li><li><TransactionRowSkeleton /></li><li><TransactionRowSkeleton /></li></ul></TransactionListCard>;
  const { expenses: { items: expenses, total }, model } = page.data;
  const pagination = <SettlementPagination offset={offset} total={total} limit={EXPENSES_PAGE_SIZE} onPage={setOffset} />;
  // A removal or a concurrent change can empty the current offset page; the controls must stay to leave it.
  if (!expenses.length) return <TransactionListEmpty aria-label={REGION} icon={ReceiptIcon} title={emptyTitle(filter !== 'all', offset > 0)} action={offset > 0 ? pagination : null} />;
  const renderExpense = (expense: SpaceExpenseRow) => <SpaceExpenseItem key={expense.id} space={space} userId={userId} expense={expense} model={model} categoryIndex={categoryIndex} onOpen={onOpen} />;
  const renderDay = (day: DayGroup<SpaceExpenseRow>) => <div key={day.key}>
    <DayHeader date={day.date} total={day.items.reduce((sum, expense) => sum + expense.sharedAmountCents, 0) / 100} tone="neutral" />
    <ul className="divide-y divide-border">{day.items.map(renderExpense)}</ul>
  </div>;
  return <TransactionListCard aria-label={REGION} label="Despesas" count={<ListTotal count={total} cents={page.data.sharedAmountCents} />} className="@container"
    footer={total > EXPENSES_PAGE_SIZE || offset > 0 ? pagination : null}>
    {groupItemsByDay(expenses, (expense) => expense.occurredAt.slice(0, 10)).map(renderDay)}
  </TransactionListCard>;
}

function ListTotal({ count, cents }: Readonly<{ count: number; cents: number }>) {
  const format = useFormatCurrency();
  return <>{count.toLocaleString('pt-BR')} {count === 1 ? 'despesa' : 'despesas'} · <span className="font-semibold text-text-primary">{format(cents / 100, { noCents: true })}</span></>;
}

function emptyTitle(filtered: boolean, beyond: boolean): string {
  if (beyond) return 'Não há mais despesas nesta página. Volte para as anteriores.';
  return filtered ? 'Nenhuma despesa com este filtro neste período.' : 'Nenhuma despesa entrou no espaço neste período.';
}

// The whole line opens the expense: a full-size button laid over it keeps one click target without turning the row's
// text into the button's name, so a screen reader still reads the line and hears "Ver detalhes de …" as the action.
// How it entered, the payer's own account and each person's part are the drawer's, one tap away: the line keeps one
// row of facts.
export function SpaceExpenseItem({ space, userId, expense, model, categoryIndex, onOpen, density }: Readonly<{
  space: SpaceDetail; userId: string; expense: SpaceExpenseRow; model: SpaceDetail['model']; categoryIndex: CategoryIndex; onOpen: (expense: SpaceExpenseRow) => void; density?: 'row' | 'compact';
}>) {
  const handleOpen = () => onOpen(expense);
  const viewer = useSpacePerspective(userId);
  const compact = density === 'compact';
  return <li className="relative">
    <SpaceExpenseLine className={TRANSACTION_LINE_INTERACTIVE} density={density} category={getCategoryMeta(categoryIndex, expense.categoryId)} name={expense.name}
      pills={<ExpensePills expense={expense} compact={compact} />} payer={expensePayerName(space, expense)} payerPhotoUrl={memberPhoto(space, expense.payerUserId)}
      share={compact ? null : <MyShare expense={expense} userId={viewer} model={model} />}
      amountCents={expense.sharedAmountCents} sub={compact ? <MyShare expense={expense} userId={viewer} model={model} standalone /> : expense.partial && expense.source ? <OriginalTotal total={expense.source.totalAmountCents} /> : null}
      trailing={<>
        <CaretRightIcon aria-hidden weight="bold" className="size-4 shrink-0 text-text-tertiary" />
        {/* Last in the DOM so it paints over the positioned avatars too. */}
        <button type="button" onClick={handleOpen} aria-label={`Ver detalhes de ${expense.name}`}
          className="absolute inset-0 cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring" />
      </>} />
  </li>;
}

// `source` only arrives for the payer; `SpacePrivate` masks it during a partner preview (VER-411).
function OriginalTotal({ total }: Readonly<{ total: number }>) {
  const format = useFormatCurrency();
  return <SpacePrivate>de {format(total / 100)}</SpacePrivate>;
}

function MyShare({ expense, userId, model, standalone = false }: Readonly<{ expense: SpaceExpenseRow; userId: string; model: SpaceDetail['model']; standalone?: boolean }>) {
  const format = useFormatCurrency();
  const mine = expense.splits.find((split) => split.userId === userId);
  if (model !== 'split' || !mine) return null;
  if (standalone) return <>sua parte {format(mine.amountCents / 100)}</>;
  return <> · sua parte {format(mine.amountCents / 100)}</>;
}
